import { useSendKey } from '../../hooks/useSendKey'
import { useState } from 'react'
import { createPortal } from 'react-dom'

import { ApiError } from '../../api/client'
import { useTextgrenzen } from '../../hooks/useTextgrenzen'
import { useEscape } from '../../hooks/useEscape'
import { useTexte } from '../../texte'
import { Blattkopf, useDiscardGuard } from '../../components/Blattkopf'

/**
 * „Musik hinzufügen" (#102, 1.57.0): ein Link hinein, eine Karte heraus.
 *
 * **Geprüft wird auf dem Server**, und deshalb bleibt das Blatt bei einer
 * Absage stehen: der Link steht noch im Feld, darunter der Satz, was an ihm
 * nicht stimmt. Ein Blatt, das zumacht und den Link mitnimmt, hieße abtippen.
 *
 * **Am Seitenkörper** (`createPortal`), wie jedes Blatt hier — sonst klebt es
 * am Verlauf und scrollt mit.
 */
export function Musikwahl({
  onSenden,
  onZu,
  wohin,
  ort,
}: {
  /** Wirft, wenn der Server den Link nicht nimmt — dann bleibt das Blatt stehen. */
  /** `client_key` (1.75.0): one key per card, not per tap -- the playlist leaves it out. */
  onSenden: (eingabe: { url: string; body: string; client_key: string }) => Promise<void>
  onZu: () => void
  /** Where the card goes (`sendeOrt`), part of its key (1.75.2). */
  ort: string
  /**
   * Wohin das Stück geht — und damit, wie lang der Satz sein darf (1.66.0,
   * aus der Codereview).
   *
   * **Dasselbe Blatt bedient zwei Wege**, den Chat (`Composer`) und die
   * Playlist (`PlaylistPage`), und ihre Grenzen sind verschieden: im Chat ist
   * der Satz der Text eines **Beitrags** (`MAX_MESSAGE_LENGTH`, Vorgabe 8000),
   * in der Playlist eine Notiz am Eintrag (200). Hier stand ein festes
   * `maxLength={200}` für beide -- im Chat also eine Kürzung um den Faktor
   * vierzig, stillschweigend und ohne Zähler.
   */
  wohin: 'chat' | 'playlist'
}) {
  const t = useTexte()
  const grenzen = useTextgrenzen()
  const grenze = wohin === 'playlist' ? grenzen.playlist_note : grenzen.message
  const [url, setUrl] = useState('')
  const [text, setText] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const sendeSchluessel = useSendKey(ort)
  const [problem, setProblem] = useState<string | null>(null)
  // A typed link is a draft: leaving with one asks first (1.196.0).
  const zu = useDiscardGuard(url.trim() !== '' || text.trim() !== '', onZu)
  useEscape(zu)

  async function sende(): Promise<void> {
    setProblem(null)
    setLaeuft(true)
    try {
      // The same link and caption keep their key while the sheet is open:
      // a second tap after a lost answer gets the card that stands.
      const eingabe = { url: url.trim(), body: text.trim() }
      await onSenden({ ...eingabe, client_key: sendeSchluessel.keyFor(JSON.stringify(eingabe)) })
      onZu()
    } catch (fehler) {
      setProblem(
        fehler instanceof ApiError && fehler.offline
          ? t.chat.musikOhneNetz
          : fehler instanceof Error
            ? fehler.message
            : t.allgemein.schiefgelaufen,
      )
    } finally {
      setLaeuft(false)
    }
  }

  return createPortal(
    <div className="bogen bogen--musik" role="dialog" aria-modal="true" aria-label={t.chat.musikTitel}>
      <div className="bogen__blatt bogen__blatt--gegliedert">
        <Blattkopf titel={t.chat.musikTitel} onSchliessen={zu} />

        {/* **Kopf, Inhalt und Fuß als Geschwister** (1.61.0), the build from
            the picker "Aus dieser Reise" (1.53.3). */}
        <div className="bogen__rolle">
          <label className="field">
            <span className="field__label">{t.chat.musikFeld}</span>
            <input
              className="field__input"
              type="url"
              inputMode="url"
              autoComplete="off"
              placeholder={t.chat.musikPlatzhalter}
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
          </label>
          <p className="feld__hinweis">{t.chat.musikHinweis}</p>

          <label className="field">
            <span className="field__label">{t.chat.musikText}</span>
            <input
              className="field__input"
              type="text"
              maxLength={grenze}
              placeholder={t.chat.musikTextPlatzhalter}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </label>

          {/* Was beim Anzeigen passiert, steht vor dem Senden da und nicht
              hinterher in einem Dokument: die Karte holt den Player, und der
              Anbieter sieht dabei jeden, der mitliest. */}
          <p className="feld__hinweis">{t.chat.musikExtern}</p>

          {problem !== null && (
            <p className="feld__hinweis" role="alert" data-musik-fehler="">
              {problem}
            </p>
          )}
        </div>

        <div className="bogen__fuss">
          <button
            type="button"
            className="button"
            disabled={url.trim() === '' || laeuft}
            onClick={() => void sende()}
            data-musik-senden=""
          >
            {t.chat.musikSenden}
          </button>
          <button type="button" className="button button--outline" onClick={zu} data-edit-cancel="">
            {t.allgemein.abbrechen}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
